import { computed, inject, Injectable, signal } from '@angular/core';
import {
  CreateOrder,
  Customer,
  DeliveryAreaDto,
  LocationDto,
  Order,
  OrderDiscType,
  OrderState,
  OrderTypeEnum,
  PaymentIntervalType,
  PromotionDto,
  ZipcodeAddressDto,
  ZipCodeCheckResultDto,
} from '../../dto';
import { AbstractStateServiceComponent, baseState, DefaultState } from '../../utils/abstract-state-service.component';
import { cloneDeep, find, isObject, kebabCase, merge } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { NumberPorting, OrderService, PackageOptionDefinition } from '@webmodule-order/api';
import { PromotionsService } from '../promotions/promotions.service';

interface State extends DefaultState {
  customer: Customer;
  location: LocationDto | null;
  address: ZipcodeAddressDto | null;
  deliveryArea: DeliveryAreaDto | null;
  zipcodeResult: ZipCodeCheckResultDto | null;
  promotions: PromotionDto[];
  order: Order;
}

const initialState: State = {
  ...baseState,
  customer: new Customer(),
  location: null,
  address: null,
  deliveryArea: null,
  zipcodeResult: null,
  promotions: [],
  order: new Order(),
};

export type UiState = 'CONFIG' | 'DETAILS' | 'COMPLETED' | 'ERROR';

@Injectable({
  providedIn: 'root',
})
export class ClientOrderService extends AbstractStateServiceComponent {
  private readonly promotionsService = inject(PromotionsService);
  private readonly apiService = inject(OrderService);
  readonly orderState = new OrderState();
  readonly order = new Order();
  readonly canOrder = computed(() => this.orderState.isOrderable());
  readonly uiState = signal<UiState>('CONFIG');
  readonly validOrderDetails = signal<boolean>(false);
  readonly pendingRequest = signal<boolean>(false);
  readonly orderType = signal<OrderTypeEnum>('NEW_CUSTOMER');
  readonly type = computed(() => this.orderType());
  readonly supplier = computed(() => {
    return {
      name: this.deliveryArea()?.description ?? 'Leverancier onbekend',
      logo: kebabCase(this.deliveryArea()?.description ?? 'multifiber'),
    };
  });
  override state = signal(initialState);
  readonly customer = computed(() => this.state().customer);
  readonly location = computed(() => this.state().location);
  readonly address = computed(() => this.state().address);
  readonly deliveryArea = computed(() => this.state().deliveryArea);

  /**
   * Update order details
   * @param {Customer} customer
   * @param {string} wishDate
   * @param {PaymentIntervalType} paymentInterval
   * @param {string} reseller
   */
  updateOrderDetails(customer: Customer, wishDate: string, paymentInterval: PaymentIntervalType, reseller: string) {
    const state = this.state();

    customer.address = {
      street: state.address?.street,
      city: state.address?.city,
      location: state.location ?? Object.create(null),
    };

    const order = this.order;
    order.wishDate = wishDate;
    order.feePayment = paymentInterval;
    order.customer = customer;
    order.reseller = reseller;

    this.state.set({
      ...state,
      customer: customer,
    });

    this.setSessionStorage('orderState');
  }

  /**
   * Reset order state
   */
  override reset() {
    super.reset();

    this.state.set({
      ...this.state(),
      order: new Order(),
      customer: new Customer(),
      location: null,
      address: null,
      deliveryArea: null,
    });
  }

  setType(type: OrderTypeEnum) {
    this.orderType.set(type);
  }

  override loadSessionState(key: string) {
    const sessionState = sessionStorage.getItem(key);
    if (!key || !sessionState) {
      return Promise.reject(`no session storage for ${key}`);
    }

    const jsonSessionState = JSON.parse(sessionStorage.getItem(key) ?? '') as State;

    if (!jsonSessionState.zipcodeResult) {
      return Promise.reject(`has session but no zipcode result`);
    }

    this.setOrderState(jsonSessionState.order);

    return Promise.resolve(jsonSessionState);
  }

  setResultDto(result: ZipCodeCheckResultDto) {
    if (!result.location || !result.postcodeNL || !result.location?.deliveryArea) {
      console.error('Something went wrong setting Zipcode check result');
      return;
    }

    const deliveryArea = result.location.deliveryArea;
    const promotions = deliveryArea.promotions ?? [];

    this.state.set({
      ...this.state(),
      location: result.location,
      address: result.postcodeNL,
      deliveryArea: deliveryArea,
      promotions: promotions,
      loaded: true,
      zipcodeResult: result,
    });

    this.promotionsService.setPromotions(promotions);
    this.setSessionStorage('orderState');
  }

  setOrderState(order: Order) {
    console.log('setOrderState', order);
    this.state.set({
      ...this.state(),
      order: order,
    });
  }

  /**
   * Update order with choices by user
   * @param item
   * @param {string} disc
   */
  updateOrder(item: any, disc: OrderDiscType) {
    if (!item) {
      disc === 'internet' && this.orderState.internet.set(false);
      disc === 'itv' && this.orderState.tv.set(false);
      disc === 'landline' && this.orderState.phone.set(false);
      disc === 'mobile' && this.orderState.mobile.set(false);
      return;
    }

    switch (disc) {
      // Internet Package
      case 'internet': {
        this.order.package = item;
        this.checkState('internet');
        break;
      }
      // TV options
      // itv can be withoutItv / withItv
      case 'itv': {
        this.order.withItv = item.id === 'withItv';
        // Reset all tv extras if order is withoutItv
        if (item.id === 'withoutItv') {
          this.order.withTvBox = false;
          this.order.packageOptionsMap.set('itvBox', null);
          this.order.tvOptions.set([]);
        }

        this.checkState('tv');
        break;
      }
      case 'itvBoxType': {
        if (!this.order.withItv) {
          // If withItv is false withTvBox cannot be set
          this.order.withTvBox = false;
        } else {
          // NOTE: there is no package option for itvBoxType
          this.order.withTvBox = item.id === 'withItvBox';
        }

        this.checkState('tv');
        break;
      }
      case 'itvBox': {
        if (!this.order.withItv) {
          this.order.packageOptionsMap.set(disc, null);
        } else {
          this.order.packageOptionsMap.set(disc, item);
        }

        this.checkState('tv');
        break;
      }
      case 'tvOptions': {
        if (item.length) {
          this.order.tvOptions.set(item);
        } else {
          this.order.tvOptions.set([]);
        }
        break;
      }
      // Phone options
      case 'landline': {
        this.order.noPhone = item.id === 'withoutLandline';

        // Reset all phone extras if order is withoutLandLine
        if (item.id === 'withoutLandline') {
          this.order.packageOptionsMap.set('phoneUnlimited', null);
          this.order.packageOptionsMap.set('numberPorting', null);
          this.order.packageOptionsMap.set('extraPhoneLine', null);
          this.order.packageOptionsMap.set('extraNumberPorting', null);
          this.order.numberPortings = [];
        }

        this.checkState('phone');
        break;
      }
      case 'numberPorting':
      case 'extraPhoneLine':
      case 'phoneUnlimited':
      case 'extraNumberPorting': {
        // Reset phone extra if order noPhone is true
        if (this.order.noPhone) {
          this.order.packageOptionsMap.set(disc, null);
        } else {
          this.order.packageOptionsMap.set(disc, item);
        }

        this.checkState('phone');
        break;
      }
      default: {
        break;
      }
    }

    // this.setSessionStorage('orderState');

    this.state.set({
      ...this.state(),
      order: this.order,
    });

    this.setSessionStorage('orderState');
  }

  submitOrder() {
    const order = cloneDeep(this.order);

    const createOrder = new CreateOrder();
    createOrder.customer = this.customer();
    createOrder.discounts = order.discounts;
    createOrder.feePayment = order.feePayment;
    createOrder.feePaymentDelivery = order.feePaymentDelivery;
    createOrder.numberPortings = order.numberPortings;
    createOrder.wishDate = order.wishDate;
    createOrder.package = order.package;

    this.updatePackageDefinitions(createOrder.package?.packageOptionDefinitions ?? []);

    createOrder.noPhone = order.noPhone ?? false;
    createOrder.reseller = order.reseller ?? '';
    createOrder.withItv = order.withItv ?? false;
    createOrder.withTvBox = order.withTvBox ?? false;

    return firstValueFrom(this.apiService.postOrder({ preview: false, body: createOrder }));
  }

  phoneNumbersToPort(phoneNumbers: NumberPorting[]) {
    this.order.numberPortings = phoneNumbers;
    this.checkState('phone');
  }

  private updatePackageDefinitions(packages: PackageOptionDefinition[]) {
    return packages.map((definition) => {
      if (definition.packageOption?.id) {
        let packageOptions = Array.from(this.order.packageOptionsMap.values()).filter((value) => value !== null);
        packageOptions = merge(packageOptions, this.order.tvOptions());
        definition.packageOption = find(packageOptions, { id: definition.packageOption.id }) ?? definition.packageOption;
      }

      return definition;
    });
  }

  private checkState(disc: 'internet' | 'phone' | 'tv' | 'mobile') {
    switch (disc) {
      case 'internet': {
        if (!this.order.package) {
          this.orderState.internet.set(false);
          return;
        }

        this.orderState.internet.set(true);
        // TV gets ignored if package is internet-only
        if ('internet-only' === kebabCase(this.order.package.type)) {
          this.orderState.tv.set(true);
        } else {
          this.checkState('tv');
        }
        break;
      }
      case 'tv': {
        if (this.order.withItv === undefined || this.order.withTvBox === undefined) {
          this.orderState.tv.set(false);
          return;
        }
        this.orderState.tv.set(true);

        break;
      }
      case 'phone': {
        if (this.order.noPhone === true) {
          this.orderState.phone.set(true);
          break;
        }

        console.log('phone 0');
        const numberPorting = this.order.packageOptionsMap.get('numberPorting');

        console.log('numberPorting', numberPorting);
        // If noPhone is true, numberPorting package becomes mandatory
        if (!numberPorting) {
          this.orderState.phone.set(false);
          break;
        }
        console.log('phone 1');
        const extraPhoneLine = this.order.packageOptionsMap.get('extraPhoneLine');
        // If extraPhoneLine is chosen, extraNumberPorting package becomes mandatory
        if (extraPhoneLine) {
          const extraNumberPorting = this.order.packageOptionsMap.get('extraNumberPorting');
          this.orderState.phone.set(isObject(extraNumberPorting));
          break;
        }

        console.log('phone 2');
        this.orderState.phone.set(true);
        break;
      }
      case 'mobile': {
        break;
      }
    }
  }
}
